// Certificates of completion — issued only from server-verified results (public.completions).
//
// Nine certificates: the Level 1 Support Desk, one per networking domain (D01–D07), and Phase 1
// (everything). The `issue-certificate` Edge Function decides eligibility with decideIssue()
// below from the learner's own completions rows, then writes public.certificates with the service
// role — learners cannot write that table. Anyone can check a certificate by its ID through the
// public function verify_certificate(code) (supabase/migrations/0004_certificates.sql), which the
// site shows at #/cert/<ID>.
//
// Pure module (no browser / Node / Deno APIs): bundled into the Edge Function by
// scripts/build-functions.mjs and used by the site and the tests.

const SUPPORT_IDS = ['TK-L1-10421', 'TK-L1-10458', 'TK-L1-10503', 'TK-L1-10577', 'TK-L1-10614'];
const DOMAINS = [
  ['d01', '01', 'Network Fundamentals', 'Manila'],
  ['d02', '02', 'LAN Switching', 'Cebu City'],
  ['d03', '03', 'Routing Technologies', 'Davao City'],
  ['d04', '04', 'WAN Technologies', 'Batangas City'],
  ['d05', '05', 'Infrastructure Services', 'Clark / Angeles'],
  ['d06', '06', 'Infrastructure Security', 'Taguig'],
  ['d07', '07', 'Infrastructure Management', 'Iloilo City'],
];
const MISSIONS_PER_DOMAIN = 14;
const missionKeys = (d) => Array.from({ length: MISSIONS_PER_DOMAIN }, (_, i) => `${d}.u${String(i + 1).padStart(2, '0')}`);

/** Every certificate: id, title, what it is for, and the verified items it needs. */
export const CERTIFICATES = [
  {
    kind: 'support', short: 'Level 1 IT Support',
    title: 'Level 1 IT Support Foundations',
    detail: 'Completed the RouteZero Level 1 Support Desk: five real-world support tickets — diagnosed with real commands, resolved or escalated, and documented — each verified by RouteZero.',
    needs: SUPPORT_IDS.map((id) => ({ kind: 'support', id })),
  },
  ...DOMAINS.map(([d, n, title, city]) => ({
    kind: d, short: `Domain ${n} · ${title}`,
    title: `Domain ${n} · ${title}`,
    detail: `Completed all ${MISSIONS_PER_DOMAIN} hands-on missions of Domain ${n} (${title}), including the final troubleshooting challenge at ${city} — each one replayed and verified by RouteZero.`,
    needs: missionKeys(d).map((id) => ({ kind: 'lab', id })),
  })),
  {
    kind: 'phase1', short: 'Phase 1 · Network Engineer Path',
    title: 'Network Engineer Path — Phase 1',
    detail: `Completed the Level 1 Support Desk and all seven networking domains — ${DOMAINS.length * MISSIONS_PER_DOMAIN} hands-on missions from fundamentals to infrastructure management — each verified by RouteZero.`,
    needs: [...SUPPORT_IDS.map((id) => ({ kind: 'support', id })), ...DOMAINS.flatMap(([d]) => missionKeys(d).map((id) => ({ kind: 'lab', id })))],
  },
];
export const CERT_KINDS = CERTIFICATES.map((c) => c.kind);
export const certOf = (kind) => CERTIFICATES.find((c) => c.kind === kind) || null;

/** How many of a certificate's items are verified: { done, total, eligible }. */
export function certProgress(kind, rows) {
  const c = certOf(kind);
  if (!c) return { done: 0, total: 0, eligible: false };
  const have = new Set((Array.isArray(rows) ? rows : []).map((r) => `${r.kind}|${r.item_id}`));
  const done = c.needs.filter((n) => have.has(`${n.kind}|${n.id}`)).length;
  return { done, total: c.needs.length, eligible: done === c.needs.length };
}

/** Name printed on a certificate: letters (any language), spaces, . ' - (2–60 characters). */
export function cleanHolderName(v) {
  const s = String(v ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
  return /^[\p{L}\p{M}][\p{L}\p{M} .'’-]{1,59}$/u.test(s) ? s : null;
}

/** Certificate IDs: RZ-XXXX-XXXX (no 0/O/1/I/L). `random` returns an integer in [0, n). */
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const CERT_ID = /^RZ-[2-9A-HJKMNP-Z]{4}-[2-9A-HJKMNP-Z]{4}$/;
export function makeCertId(random) {
  const part = () => Array.from({ length: 4 }, () => ALPHABET[random(ALPHABET.length)]).join('');
  return `RZ-${part()}-${part()}`;
}
export const normalizeCertId = (v) => String(v ?? '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');

/**
 * One issue request → response. Pure; the Edge Function adds auth and storage.
 * @param {{ userId: string|null, bodyText: string, rows: object[], existing: object|null, newId: () => string, now?: string }} o
 *   rows: this learner's completions · existing: their certificate of that kind, if any
 * @returns {{ status:number, body:object, insert: object|null }}
 */
export function decideIssue({ userId, bodyText, rows, existing, newId, now = new Date().toISOString() }) {
  if (!userId) return { status: 401, body: { error: 'Sign in first.' }, insert: null };
  if (typeof bodyText !== 'string' || bodyText.length > 4096) return { status: 413, body: { error: 'Request too large.' }, insert: null };
  let body;
  try { body = JSON.parse(bodyText); } catch { return { status: 400, body: { error: 'Invalid JSON.' }, insert: null }; }
  const kind = String(body?.kind || '');
  if (!CERT_KINDS.includes(kind)) return { status: 400, body: { error: 'Unknown certificate.' }, insert: null };
  if (existing) return { status: 200, body: { certificate: existing, already: true }, insert: null };
  const p = certProgress(kind, rows);
  if (!p.eligible) return { status: 403, body: { error: 'Not complete yet.', done: p.done, total: p.total }, insert: null };
  const name = cleanHolderName(body?.name);
  if (!name) return { status: 400, body: { error: 'Enter the name to print: letters, spaces, apostrophes or hyphens (2–60 characters).' }, insert: null };
  const certificate = { id: newId(), user_id: userId, kind, holder_name: name, issued_at: now };
  return { status: 200, body: { certificate }, insert: certificate };
}
